import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Target, Calculator, Info, Receipt, Users, Wallet } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

const CreateGroup = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [groupType, setGroupType] = useState('expense'); // 'expense' or 'savings'
  
  const [formData, setFormData] = useState({
    name: '',
    goal_amount: '',
    timeline_days: '',
    num_members: '2',
    contribution_frequency: 'one-time',
    dao_type: 'regular'
  });
  
  const [calculated, setCalculated] = useState({
    perMemberTotal: 0,
    perContribution: 0,
    totalContributions: 0
  });

  const calculateContributions = () => {
    if (groupType === 'expense') {
      setCalculated({ perMemberTotal: 0, perContribution: 0, totalContributions: 0 });
      return;
    }
    
    const goal = parseFloat(formData.goal_amount) || 0;
    const members = parseInt(formData.num_members) || 1;
    const days = parseInt(formData.timeline_days) || 1;

    if (goal <= 0 || members <= 0 || days <= 0) {
      setCalculated({ perMemberTotal: 0, perContribution: 0, totalContributions: 0 });
      return;
    }

    const perMemberTotal = goal / members;
    let perContribution = 0;
    let totalContributions = 0;

    if (formData.contribution_frequency === 'daily') {
      totalContributions = days;
      perContribution = perMemberTotal / days;
    } else if (formData.contribution_frequency === 'weekly') {
      totalContributions = Math.ceil(days / 7);
      perContribution = perMemberTotal / totalContributions;
    } else if (formData.contribution_frequency === 'one-time') {
      totalContributions = 1;
      perContribution = perMemberTotal;
    }

    setCalculated({
      perMemberTotal: perMemberTotal.toFixed(2),
      perContribution: perContribution.toFixed(2),
      totalContributions
    });
  };

  useEffect(() => {
    calculateContributions();
  }, [formData.goal_amount, formData.num_members, formData.timeline_days, formData.contribution_frequency, groupType]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Please enter a group name');
      return;
    }
    
    if (groupType === 'savings') {
      if (!formData.goal_amount || !formData.timeline_days || !formData.num_members) {
        toast.error('Please fill all required fields');
        return;
      }
      if (parseFloat(calculated.perContribution) <= 0) {
        toast.error('Invalid contribution calculation');
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        num_members: parseInt(formData.num_members) || 10,
        dao_type: groupType === 'expense' ? 'expense_only' : formData.dao_type
      };
      
      if (groupType === 'savings') {
        payload.goal_amount = parseFloat(formData.goal_amount);
        payload.timeline_days = parseInt(formData.timeline_days);
        payload.contribution_frequency = formData.contribution_frequency;
        payload.daily_contribution = parseFloat(calculated.perContribution);
      } else {
        // For expense-only groups, set minimal defaults
        payload.goal_amount = 0;
        payload.timeline_days = 365;
        payload.contribution_frequency = 'one-time';
        payload.daily_contribution = 0;
      }
      
      const response = await axios.post(`${API}/groups?user_id=${user.id}`, payload);
      toast.success('Group created successfully!');
      navigate(`/group/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950">
      <div className="bg-card/80 backdrop-blur-xl border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Back</span>
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="create-group-title">Create Group</h1>
            <p className="text-sm sm:text-base text-muted-foreground">Choose your group type to get started</p>
          </div>

          {/* Group Type Selection */}
          <Tabs value={groupType} onValueChange={setGroupType} className="w-full mb-6">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="expense" className="text-xs sm:text-sm">
                <Receipt className="w-4 h-4 mr-1 sm:mr-2" /> 
                <span className="hidden sm:inline">Expense</span> Splitting
              </TabsTrigger>
              <TabsTrigger value="savings" className="text-xs sm:text-sm">
                <Target className="w-4 h-4 mr-1 sm:mr-2" /> 
                <span className="hidden sm:inline">Savings</span> Goal
              </TabsTrigger>
            </TabsList>

            {/* Expense Splitting Form */}
            <TabsContent value="expense">
              <div className="bg-card rounded-2xl p-4 sm:p-6 border border-border">
                <div className="flex items-start gap-3 mb-4 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-xl">
                  <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                  <p className="text-xs sm:text-sm text-blue-700 dark:text-blue-300">
                    Perfect for splitting bills, trips, rent, or any shared expenses. No savings goal required.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-sm">Group Name *</Label>
                    <Input
                      id="name"
                      placeholder="e.g., Goa Trip, Roommates, Office Lunch"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className="rounded-xl mt-1.5 text-sm"
                      data-testid="group-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="num_members" className="text-sm">Max Members</Label>
                    <Input
                      id="num_members"
                      type="number"
                      placeholder="10"
                      value={formData.num_members}
                      onChange={(e) => handleChange('num_members', e.target.value)}
                      className="rounded-xl mt-1.5 text-sm"
                      data-testid="num-members-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">You can invite members after creating the group</p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full rounded-full py-5 sm:py-6 font-semibold gradient-primary text-sm sm:text-base"
                    disabled={loading}
                    data-testid="submit-create-group-btn"
                  >
                    {loading ? 'Creating...' : 'Create Expense Group'}
                  </Button>
                </form>

                {/* Features */}
                <div className="mt-6 pt-6 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-3">What you can do:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {['Split bills equally', 'Custom splits', 'Track who owes', 'Settle via UPI'].map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                        <div className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Savings Goal Form */}
            <TabsContent value="savings">
              <div className="bg-card rounded-2xl p-4 sm:p-6 border border-border">
                <div className="flex items-start gap-3 mb-4 p-3 bg-green-50 dark:bg-green-950/30 rounded-xl">
                  <Target className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <p className="text-xs sm:text-sm text-green-700 dark:text-green-300">
                    Set a savings goal and track contributions from all members towards the target.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-sm">Group Name *</Label>
                    <Input
                      id="name"
                      placeholder="e.g., Festival Fund, Emergency Fund"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className="rounded-xl mt-1.5 text-sm"
                      data-testid="group-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="dao_type" className="text-sm">Group Type</Label>
                    <Select
                      value={formData.dao_type}
                      onValueChange={(value) => handleChange('dao_type', value)}
                    >
                      <SelectTrigger className="rounded-xl mt-1.5 text-sm" data-testid="dao-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="regular">Regular Savings</SelectItem>
                        <SelectItem value="emergency">Emergency Fund</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor="goal_amount" className="text-sm">Goal (₹) *</Label>
                      <Input
                        id="goal_amount"
                        type="number"
                        placeholder="50000"
                        value={formData.goal_amount}
                        onChange={(e) => handleChange('goal_amount', e.target.value)}
                        className="rounded-xl mt-1.5 text-sm"
                        data-testid="goal-amount-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="num_members" className="text-sm">Members *</Label>
                      <Input
                        id="num_members"
                        type="number"
                        placeholder="5"
                        value={formData.num_members}
                        onChange={(e) => handleChange('num_members', e.target.value)}
                        className="rounded-xl mt-1.5 text-sm"
                        data-testid="num-members-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="timeline_days" className="text-sm">Timeline (Days) *</Label>
                    <Input
                      id="timeline_days"
                      type="number"
                      placeholder="90"
                      value={formData.timeline_days}
                      onChange={(e) => handleChange('timeline_days', e.target.value)}
                      className="rounded-xl mt-1.5 text-sm"
                      data-testid="timeline-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="frequency" className="text-sm">Contribution Frequency</Label>
                    <Select
                      value={formData.contribution_frequency}
                      onValueChange={(value) => handleChange('contribution_frequency', value)}
                    >
                      <SelectTrigger className="rounded-xl mt-1.5 text-sm" data-testid="frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="one-time">One-Time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Live Calculator */}
                  {calculated.perContribution > 0 && (
                    <div className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 rounded-xl p-4 border border-violet-200 dark:border-violet-800">
                      <div className="flex items-center gap-2 mb-2">
                        <Calculator className="w-4 h-4 text-violet-600" />
                        <span className="text-xs font-medium text-violet-700 dark:text-violet-300">Per Member</span>
                      </div>
                      <p className="text-2xl sm:text-3xl font-bold text-violet-600">₹{calculated.perContribution}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        per {formData.contribution_frequency === 'one-time' ? 'payment' : formData.contribution_frequency === 'daily' ? 'day' : 'week'}
                      </p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full rounded-full py-5 sm:py-6 font-semibold gradient-primary text-sm sm:text-base"
                    disabled={loading}
                    data-testid="submit-create-group-btn"
                  >
                    {loading ? 'Creating...' : 'Create Savings Group'}
                  </Button>
                </form>
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
};

export default CreateGroup;
